import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchSubjects } from "../../redux/subject/subjectActions";
import { fetchPrograms } from '../../redux/program/programActions';
import Loader from "../../components/Loader/Loader";
import Table from "../../components/Table/Table";
import Pagination from "../../components/Pagination/Pagination";
import BulkUploadTeacherSubjectProgram from "./BulkUploadTeacherSubjectProgram";
import * as XLSX from 'xlsx';
import { enqueueSnackbar } from "notistack";
import ExcelSample from "./ExcelSample.json";
import { getUsers } from "../../services/UserService";
import { updateTeacherPassword, bulkUploadTeachersClassSubject } from "../../services/TeacherSubjectMappingService";
import { debounce } from 'lodash'; // Import debounce from lodash
import Modal from "../../components/Modal/Modal";
import BulkUploadGuideLine from "./BulkUploadGuideLine";
import TeacherFilter from "./TeacherFilter";
import { tableConstants } from "./teacherTableConstant";

const TeacherList = ({ history }) => {
  const [newPassword, setNewPassword] = useState("");
  const [resetPasswordUserId, setResetPasswordUserId] = useState(null);
  const permissions = JSON.parse(localStorage.getItem("permissions"));
  const [name, setName] = useState("");
  const [isBulk, setIsBulk] = useState(false);
  const [file, setFile] = useState(null);
  const [info, setInfo] = useState(false);

  const dispatch = useDispatch();
  const programData = useSelector((state) => state.program.programs.data) ?? [];
  const teacherDataFromStore = useSelector((state) => state.teacher.teachers.data) ?? [];
  const subjectData = useSelector((state) => state.subject.subjects.data) ?? [];

  const [data, setData] = useState([]);
  const [totalPage, setTotalPage] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loader, setLoader] = useState(false);

  const fetchUserList = useCallback(async (name, page) => {
    try {
      setLoader(true);
      setCurrentPage(page);
      const response = await getUsers({ name, page, includeSubjectMapping: true });
      if (response.success) {
        setData(response.data);
        setTotalPage(response.last_page);
      }
    }
    catch (error) {
      console.error(error);
    }
    finally {
      setLoader(false);
    }
  }, []);

  useEffect(() => {
    if (!localStorage.getItem("organization_id")) {
      enqueueSnackbar("Select any organization!", { variant: "error" });
    }
    fetchUserList("", 1);
    dispatch(fetchPrograms({ selected: 0 }));
    dispatch(fetchSubjects({ selected: 0 }));
  }, [dispatch, fetchUserList]);

  // Debounced search function
  const handleSearch = useMemo(() => debounce((value) => {
    fetchUserList(value, 1);
  }, 500), [fetchUserList]);

  useEffect(() => {
    return () => {
      handleSearch.cancel();
    };
  }, [handleSearch]);

  const handleInputChange = (e) => {
    const { value } = e.target;
    setName(value);
    if (value.length > 2 || value.length === 0)
      handleSearch(value); // Trigger the debounced search
  };

  const resetPasswordChangeHandler = (e) => {
    setNewPassword(e.target.value);
  };

  const resetPasswordHandler = async (e, id) => {
    e.preventDefault();

    try {
      // ✅ Client-side validation
      if (!newPassword) {
        enqueueSnackbar("Please enter a new password", { variant: "error" });
        return;
      }
      else if (newPassword.length < 6) {
        enqueueSnackbar("Password must be at least 6 characters long", { variant: "error" });
        return;
      }

      // ✅ API call
      const response = await updateTeacherPassword(id, { new_password: newPassword });

      if (response.status === 200) {
        enqueueSnackbar(response.data.message.success || "Password updated successfully", { variant: "success" });
        setResetPasswordUserId(null);
      }
    } catch (error) {
      // ✅ Handle backend validation or general API errors
      const res = error?.response?.data;

      if (res?.errors) {
        // Laravel validation errors — display all of them
        const allErrors = Object.values(res.errors).flat();
        allErrors.forEach((msg) => enqueueSnackbar(msg, { variant: "error" }));
      } else if (res?.message) {
        // Laravel general error message
        enqueueSnackbar(res.message, { variant: "error" });
      } else {
        // Fallback for unexpected issues (network, server down, etc.)
        enqueueSnackbar("Something went wrong. Please try again.", { variant: "error" });
      }
    }
  };


  const resetPasswordStatusHandler = (id) => {
    setResetPasswordUserId(id);
  };

  const excelFileReader = (e) => {
    const session_id = localStorage.getItem('session_id');
    const organization_id = localStorage.getItem('organization_id');

    const reader = new FileReader();

    reader.onload = (event) => {
      const data = event.target.result;
      const workbook = XLSX.read(data, { type: 'binary' });

      const result = {};

      workbook.SheetNames.forEach(sheet => {
        const json_data = XLSX.utils.sheet_to_json(workbook.Sheets[sheet]);
        result[sheet] = json_data;
      });

      let final_data = result['Sheet1'];

      let classNameIdMap = [];
      let subjectNameIdMap = [];

      programData.forEach((item) => {
        let key = item.name && item.name.toLowerCase().replace(/\s/g, '');
        classNameIdMap[key] = item.id;
      });
      subjectData.forEach((item) => {
        let key = item.name.toLowerCase().replace(/\s/g, '');
        subjectNameIdMap[key] = item.id;
      });

      let finalData = final_data.map((item) => {
        let className = item.class.toLowerCase().replace(/\s/g, '');
        let teacher_email = item.teacher_email;
        let subjectName = item.subject_name.toLowerCase().replace(/\s/g, '');
        let program_id = classNameIdMap[className];
        let subject_id = subjectNameIdMap[subjectName];
        return {
          teacher_email, subject_id, program_id, organization_id, session_id,
        };
      });

      setFile(finalData);
    };
    reader.readAsBinaryString(e.target.files[0]);
  };

  const bulkUpload = async () => {
    if (file === null || file === undefined) {
      enqueueSnackbar("Please select a file", { variant: "error" });
      return;
    } else {
      let data = { file };
      try {
        setLoader(true);
        const response = await bulkUploadTeachersClassSubject(data);
        if (response.status === 200) {
          setLoader(false);
          enqueueSnackbar(response.data.message.success, { variant: "success" });
          history.push('/teacher/list');
        }
      } catch (error) {
        setLoader(false);
      }
    }
  };

  const downloadExcel = (e) => {
    e.preventDefault();
    const ws = XLSX.utils.json_to_sheet(ExcelSample);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, `teacher-subject-mapping-sample.xlsx`);
  };

  const downloadTeacherExcel = (e) => {
    e.preventDefault();
    const ws = XLSX.utils.json_to_sheet(teacherDataFromStore);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, `teacher-list.xlsx`);
  };

  const handlePageChange = (i) => {
    const page = i.selected + 1;
    fetchUserList(name, page);
  }

  return (
    <React.Fragment>
      {loader ? (<Loader />) :
        (<div className="card">
          <div className="card-header">
            <TeacherFilter
              name={name}
              handleInputChange={handleInputChange}
              info={info}
              setInfo={setInfo}
              isBulk={isBulk}
              setIsBulk={setIsBulk}
              downloadTeacherExcel={downloadTeacherExcel}
              permissions={permissions}
            />
          </div>
          <Modal
            isOpen={info}
            onClose={() => setInfo(false)}
            title="Info"
          >
            <BulkUploadGuideLine />
          </Modal>
          {isBulk ? (
            <BulkUploadTeacherSubjectProgram
              downloadExcel={downloadExcel}
              excelFileReader={excelFileReader}
              bulkUpload={bulkUpload}
            />
          ) : (
            <div className="card-body evaluating-table">
              {data && data.length ? (
                <div>
                  <Table
                    cols={tableConstants(
                      resetPasswordHandler,
                      resetPasswordStatusHandler,
                      resetPasswordChangeHandler,
                      resetPasswordUserId,
                      permissions,
                      currentPage
                    )}
                    data={data}
                    hoverable={true}
                    striped={true}
                  />
                  <Pagination
                    totalPage={totalPage}
                    currentPage={currentPage - 1}
                    handlePageChange={(i) => handlePageChange(i)}
                  ></Pagination>
                </div>
              ) : null}
            </div>
          )}
        </div>
        )}
    </React.Fragment>
  );
};

export default TeacherList;
